import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  COMMENT_SORTS,
  CommentCard,
  flattenComments,
  useRobotComments,
  type CommentItem,
  type CommentSort,
  type CommentsSummary,
} from "@/entities/comment";
import { ReportSheet } from "@/features/report-comment";
import { useVoteHelpful } from "@/features/vote-helpful";
import { CommentComposer, useDeleteComment } from "@/features/write-comment";
import { Button, Icon, Modal, Panel, Segmented, StarRating, useToast } from "@/shared/ui";

/**
 * Блок отзывов на карточке робота (доработки §6).
 *
 * **Подпись «оставить отзыв может только тот, кто запускал робота» стоит на
 * уровне блока, а не бейджем в каждой строке.** §6.1 просил бейдж «Пользователь
 * робота», но в этой реализации писать могут только владельцы — значит бейдж
 * стоял бы у всех и не различал бы никого. Как social proof утверждение о всей
 * ленте сильнее: оно говорит, что здесь нет отзывов «мимо проходил».
 *
 * **Три вкладки не сбрасывают список в скелет.** `placeholderData` в запросе
 * держит предыдущую вкладку на экране: данные почти те же, и мигание было бы
 * платой ни за что.
 *
 * **«Показать ещё» кнопкой, а не бесконечной прокруткой.** Наблюдателя
 * пересечения в приложении нет нигде, и заводить его здесь незачем — тем более
 * что у «Полезных» ключ сортировки изменчив, и самодогружающийся список
 * переставлял бы карточки под пальцем.
 */
interface RobotCommentsProps {
  robotId: number;
  summary: CommentsSummary;
}

export function RobotComments({ robotId, summary }: RobotCommentsProps) {
  const { t } = useTranslation();
  const toast = useToast();

  const [sort, setSort] = useState<CommentSort>("helpful");
  const [composing, setComposing] = useState<"new" | CommentItem | null>(null);
  const [reporting, setReporting] = useState<number | null>(null);
  const [deleting, setDeleting] = useState<CommentItem | null>(null);

  const query = useRobotComments(robotId, sort);
  const vote = useVoteHelpful(robotId, sort);
  const remove = useDeleteComment(robotId);
  const rows = flattenComments(query.data?.pages);

  return (
    <section className="flex flex-col gap-3">
      <header className="flex flex-col gap-1">
        <div className="flex items-baseline justify-between gap-3">
          <h2 className="text-body font-semibold text-text-primary">
            {t("robots.comments.title")}
          </h2>
          {summary.average_rating === null ? null : (
            <span className="flex items-center gap-1.5">
              <StarRating value={Math.round(Number(summary.average_rating))} />
              <span className="text-caption font-semibold tabular-nums text-text-primary">
                {summary.average_rating}
              </span>
            </span>
          )}
        </div>
        <p className="text-micro font-normal text-text-tertiary">
          {t("robots.comments.subtitle")}
        </p>
      </header>

      {summary.can_comment ? (
        <Button
          variant="secondary"
          size="lg"
          block
          data-haptic="press"
          onClick={() => {
            setComposing("new");
          }}
        >
          {t("robots.comments.write")}
        </Button>
      ) : summary.cannot_comment_reason === null ? null : (
        <Panel radius="lg" className="px-4 py-3">
          <p className="text-caption font-normal text-text-secondary">
            {t(`robots.comments.cannot.${summary.cannot_comment_reason}`)}
          </p>
        </Panel>
      )}

      {summary.count === 0 && rows.length === 0 ? (
        <Panel radius="lg" className="px-4 py-5">
          <p className="text-caption font-normal text-text-secondary">
            {t("robots.comments.empty")}
          </p>
        </Panel>
      ) : (
        <>
          <Segmented
            options={COMMENT_SORTS.map((code) => ({
              value: code,
              label: t(`robots.comments.sort.${code}`),
            }))}
            value={sort}
            onChange={setSort}
            ariaLabel={t("robots.comments.title")}
          />

          {rows.length === 0 ? (
            <Panel radius="lg" className="px-4 py-5">
              <p className="text-caption font-normal text-text-secondary">
                {t("robots.comments.emptySorted")}
              </p>
            </Panel>
          ) : (
            <div className="flex flex-col gap-2.5">
              {rows.map((comment) => (
                <CommentCard
                  key={comment.id}
                  comment={comment}
                  actions={
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 pt-0.5">
                      <button
                        type="button"
                        data-haptic="tap"
                        disabled={vote.isPending}
                        onClick={() => {
                          vote.mutate(comment.id);
                        }}
                        aria-pressed={comment.is_helpful}
                        className={[
                          "press flex items-center gap-1.5 text-caption font-medium",
                          comment.is_helpful ? "text-action-primary" : "text-text-secondary",
                        ].join(" ")}
                      >
                        <Icon name="checkFat" size={13} />
                        {t("robots.comments.helpful")}
                        {comment.helpful_count > 0 ? (
                          <span className="tabular-nums">{comment.helpful_count}</span>
                        ) : null}
                      </button>

                      {comment.can_edit ? (
                        <button
                          type="button"
                          data-haptic="tap"
                          onClick={() => {
                            setComposing(comment);
                          }}
                          className="press text-caption font-medium text-text-secondary"
                        >
                          {t("robots.comments.edit")}
                        </button>
                      ) : null}

                      {comment.can_delete ? (
                        <button
                          type="button"
                          data-haptic="tap"
                          onClick={() => {
                            setDeleting(comment);
                          }}
                          className="press text-caption font-medium text-status-loss"
                        >
                          {t("robots.comments.delete")}
                        </button>
                      ) : comment.author.is_me ? null : (
                        <button
                          type="button"
                          data-haptic="tap"
                          disabled={comment.is_reported}
                          onClick={() => {
                            setReporting(comment.id);
                          }}
                          className="press text-caption font-medium text-text-tertiary"
                        >
                          {comment.is_reported
                            ? t("robots.comments.reported")
                            : t("robots.comments.report")}
                        </button>
                      )}
                    </div>
                  }
                />
              ))}
            </div>
          )}

          {query.hasNextPage ? (
            <Button
              variant="ghost"
              size="lg"
              block
              data-haptic="tap"
              loading={query.isFetchingNextPage}
              onClick={() => {
                void query.fetchNextPage();
              }}
            >
              {t("robots.comments.more")}
            </Button>
          ) : null}
        </>
      )}

      {composing === null ? null : (
        <CommentComposer
          robotId={robotId}
          {...(composing === "new" ? {} : { comment: composing })}
          onClose={() => {
            setComposing(null);
          }}
        />
      )}

      {reporting === null ? null : (
        <ReportSheet
          commentId={reporting}
          onClose={() => {
            setReporting(null);
          }}
          onSent={() => {
            setReporting(null);
            toast.show({ tone: "profit", title: t("robots.comments.reported") });
          }}
        />
      )}

      {deleting === null ? null : (
        <Modal
          tone="danger"
          icon="warningFill"
          title={t("robots.comments.deleteTitle")}
          body={t("robots.comments.deleteBody")}
          onClose={() => {
            setDeleting(null);
          }}
          footer={
            <div className="flex flex-col gap-2">
              <Button
                variant="danger"
                size="cta"
                block
                data-haptic="commit"
                loading={remove.isPending}
                onClick={() => {
                  remove.mutate(deleting.id, {
                    onSettled: () => {
                      setDeleting(null);
                    },
                  });
                }}
              >
                {t("robots.comments.delete")}
              </Button>
              <Button
                variant="ghost"
                size="cta"
                block
                onClick={() => {
                  setDeleting(null);
                }}
              >
                {t("common.cancel")}
              </Button>
            </div>
          }
        />
      )}
    </section>
  );
}
